import React, { useState, useEffect } from 'react';
import { Webhook, Copy, CheckCircle, RefreshCw, ExternalLink, AlertCircle, MessageCircle, XCircle, Save, Eye, EyeOff, Settings } from 'lucide-react';

const WebhookConfig = () => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookToken, setWebhookToken] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingToken, setSavingToken] = useState(false);
  const [tokenSaved, setTokenSaved] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState({ active: false, configured: false });
  
  // Estado para configuração manual da Evolution API
  const [showEvolutionConfig, setShowEvolutionConfig] = useState(false);
  const [evolutionConfig, setEvolutionConfig] = useState({
    url: '',
    key: '',
    instance: 'a2'
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMessage, setConfigMessage] = useState(null);

  useEffect(() => {
    fetchWebhookConfig();
    fetchWebhookLogs();
    checkWhatsappStatus();
    
    // Auto-refresh logs every 30 seconds
    const interval = setInterval(() => {
      fetchWebhookLogs();
      checkWhatsappStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkWhatsappStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/whatsapp-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setWhatsappStatus(data);
    } catch (error) {
      console.error('Erro ao verificar status do WhatsApp:', error);
      setWhatsappStatus({ active: false, configured: false });
    }
  };

  // Carregar configuração da Evolution API
  const loadEvolutionConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/evolution-config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          setEvolutionConfig({
            url: data.url || '',
            key: data.key || '',
            instance: data.instance || 'a2'
          });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  // Salvar configuração da Evolution API
  const saveEvolutionConfig = async () => {
    if (!evolutionConfig.url || !evolutionConfig.key) {
      setConfigMessage({ type: 'error', text: 'URL e API Key são obrigatórios!' });
      return;
    }

    setSavingConfig(true);
    setConfigMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/evolution-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(evolutionConfig)
      });

      if (response.ok) {
        setConfigMessage({ type: 'success', text: '✅ Configuração salva com sucesso!' });
        checkWhatsappStatus(); // Atualizar status
        setShowEvolutionConfig(false);
      } else {
        const error = await response.json();
        setConfigMessage({ type: 'error', text: `❌ Erro: ${error.detail || 'Falha ao salvar'}` });
      }
    } catch (error) {
      setConfigMessage({ type: 'error', text: `❌ Erro: ${error.message}` });
    } finally {
      setSavingConfig(false);
    }
  };

  // Testar conexão da Evolution API
  const testEvolutionConnection = async () => {
    setSavingConfig(true);
    setConfigMessage(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-whatsapp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          phone: '5583998275946',
          message: '🧪 Teste de conexão Evolution API - Se você recebeu, está funcionando!'
        })
      });

      if (response.ok) {
        setConfigMessage({ type: 'success', text: '✅ Mensagem de teste enviada! Verifique seu WhatsApp.' });
        checkWhatsappStatus();
      } else {
        const error = await response.json();
        setConfigMessage({ type: 'error', text: `❌ Erro: ${error.detail || 'Falha no teste'}` });
      }
    } catch (error) {
      setConfigMessage({ type: 'error', text: `❌ Erro: ${error.message}` });
    } finally {
      setSavingConfig(false);
    }
  };

  useEffect(() => {
    loadEvolutionConfig();
  }, []);

  const fetchWebhookConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/webhook-config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Admin-Request': 'stakemind-admin'
        }
      });
      const data = await response.json();
      setWebhookUrl(data.webhook_url);
      setWebhookToken(data.webhook_token && data.webhook_token !== '***hidden***' ? data.webhook_token : '');
      setHasToken(data.has_token || false);
    } catch (error) {
      console.error('Erro ao buscar configuração do webhook:', error);
    }
  };

  const saveWebhookToken = async () => {
    if (!webhookToken || webhookToken.length < 10) {
      alert('Cole o token gerado pelo Asaas (mínimo 10 caracteres)');
      return;
    }
    setSavingToken(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/webhook-config/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Admin-Request': 'stakemind-admin'
        },
        body: JSON.stringify({ token: webhookToken })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Falha ao salvar token');
      }
      setTokenSaved(true);
      setHasToken(true);
      setTimeout(() => setTokenSaved(false), 3000);
    } catch (error) {
      alert(`Erro: ${error.message}`);
    } finally {
      setSavingToken(false);
    }
  };

  const fetchWebhookLogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/webhook-logs`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Erro ao buscar logs do webhook:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'error':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const [fixingUsers, setFixingUsers] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const handleFixAllUsers = async () => {
    if (!window.confirm('🚨 CORREÇÃO CRÍTICA\n\nEsta ação irá criar registros de access_keys para todos os usuários que não conseguem fazer login.\n\nDeseja continuar?')) {
      return;
    }

    setFixingUsers(true);
    setFixResult(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/fix-all-users-access-keys`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setFixResult({
          success: true,
          message: data.message,
          results: data.results
        });
        alert(`✅ SUCESSO!\n\n${data.message}\n\nDetalhes:\n- Total: ${data.results.total_users}\n- Corrigidos: ${data.results.fixed}\n- Já estavam OK: ${data.results.already_ok}\n- Erros: ${data.results.errors.length}`);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao executar correção');
      }
    } catch (error) {
      setFixResult({
        success: false,
        message: error.message
      });
      alert(`❌ ERRO!\n\n${error.message}`);
    } finally {
      setFixingUsers(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* 🚨 BOTÃO DE CORREÇÃO CRÍTICA */}
      <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl">
        <div className="flex items-start gap-3 sm:gap-4">
          <AlertCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white flex-shrink-0" />
          <div className="flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2">
              🚨 Correção: Login de Usuários
            </h2>
            <p className="text-white/90 text-sm sm:text-base mb-4">
              Se usuários não conseguem fazer login após receber credenciais, clique abaixo para corrigir automaticamente.
            </p>
            <button
              onClick={handleFixAllUsers}
              disabled={fixingUsers}
              className="w-full sm:w-auto bg-white text-red-600 font-bold px-6 py-3 rounded-lg hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-xl"
            >
              {fixingUsers ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                  Processando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  CORRIGIR TODOS OS USUÁRIOS
                </>
              )}
            </button>
            {fixResult && (
              <div className={`mt-4 p-3 rounded-lg ${fixResult.success ? 'bg-green-500/20 border border-green-300' : 'bg-red-500/20 border border-red-300'}`}>
                <p className="font-semibold text-white text-sm">{fixResult.message}</p>
                {fixResult.results && (
                  <ul className="mt-2 text-sm text-white/90 space-y-1">
                    <li>✅ Total: {fixResult.results.total_users}</li>
                    <li>🔧 Corrigidos: {fixResult.results.fixed}</li>
                    <li>✓ OK: {fixResult.results.already_ok}</li>
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Webhook URL Configuration */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-500/20 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
            <Webhook className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg sm:text-2xl font-bold text-white">Configuração de Webhook</h2>
            <p className="text-xs sm:text-sm text-gray-400">Configure no painel da Asaas</p>
          </div>
        </div>

        {/* URL do Webhook */}
        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
              URL do Webhook (cole no campo "URL do Webhook" da Asaas)
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={webhookUrl}
                readOnly
                data-testid="webhook-url-input"
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-slate-900 border border-slate-700 rounded-lg sm:rounded-xl text-white font-mono text-xs sm:text-sm break-all"
              />
              <button
                onClick={copyToClipboard}
                data-testid="webhook-url-copy-btn"
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-cyan-500 text-white rounded-lg sm:rounded-xl hover:bg-cyan-600 transition-colors flex items-center justify-center gap-2 font-medium text-sm"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    <span>Copiar</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Token de Autenticação */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
              Token de Autenticação (gerado pelo Asaas — clique em "Gerar Token" no painel da Asaas e cole aqui)
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={webhookToken}
                onChange={(e) => setWebhookToken(e.target.value)}
                placeholder="Cole aqui o token gerado pelo Asaas (ex: whsec_...)"
                data-testid="webhook-token-input"
                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-slate-900 border border-slate-700 rounded-lg sm:rounded-xl text-white font-mono text-xs sm:text-sm break-all focus:outline-none focus:border-purple-500"
              />
              <button
                onClick={saveWebhookToken}
                disabled={savingToken}
                data-testid="webhook-token-save-btn"
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-purple-500 text-white rounded-lg sm:rounded-xl hover:bg-purple-600 transition-colors flex items-center justify-center gap-2 font-medium text-sm disabled:opacity-50"
              >
                {savingToken ? (
                  <span>Salvando...</span>
                ) : tokenSaved ? (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Salvo!</span>
                  </>
                ) : (
                  <span>Salvar Token</span>
                )}
              </button>
            </div>
            {hasToken && !tokenSaved && (
              <p className="text-xs text-green-400 mt-2">✅ Token configurado e ativo</p>
            )}
            {!hasToken && (
              <p className="text-xs text-yellow-400 mt-2">⚠️ Nenhum token configurado — webhook não validará autenticação</p>
            )}
          </div>

          {/* Instruções */}
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div className="space-y-2">
                <h3 className="text-white font-bold text-sm">Como Configurar (Asaas):</h3>
                <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Acesse o painel da Asaas em <strong>Integrações → Webhooks → Adicionar Webhook</strong></li>
                  <li>Ative o webhook (toggle "Este Webhook ficará ativo?")</li>
                  <li><strong>Nome:</strong> Stake Mind</li>
                  <li><strong>URL:</strong> cole a URL acima (azul) — atenção: termina em <code className="text-cyan-300">/api/webhooks/asaas</code> e <strong>não</strong> <code className="text-red-400">/mercadopago</code></li>
                  <li><strong>Versão da API:</strong> v3</li>
                  <li><strong>Tipo de envio:</strong> Sequencial</li>
                  <li>Clique em <strong>Gerar Token</strong> no campo "Token de autenticação" do Asaas e copie o token gerado (ex: <code className="text-cyan-300">whsec_...</code>)</li>
                  <li><strong>Volte aqui</strong> e cole o token no campo roxo acima, clicando em <strong>Salvar Token</strong></li>
                  <li>No Asaas, em <strong>Eventos / Cobranças</strong>, marque: <code className="text-cyan-300">PAYMENT_CONFIRMED</code>, <code className="text-cyan-300">PAYMENT_RECEIVED</code>, <code className="text-cyan-300">PAYMENT_OVERDUE</code>, <code className="text-cyan-300">PAYMENT_REFUNDED</code></li>
                  <li>Clique em <strong>Salvar</strong> no Asaas</li>
                </ol>
                <a
                  href="https://www.asaas.com/customerConfigIntegrations/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium mt-2"
                  data-testid="asaas-panel-link"
                >
                  <span>Ir para painel da Asaas</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status do Evolution API (WhatsApp) */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 ${whatsappStatus.active ? 'bg-green-500/20' : 'bg-red-500/20'} rounded-xl flex items-center justify-center`}>
              <MessageCircle className={`w-5 h-5 sm:w-6 sm:h-6 ${whatsappStatus.active ? 'text-green-400' : 'text-red-400'}`} />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">Status WhatsApp (Evolution API)</h3>
              <p className="text-xs sm:text-sm text-gray-400">Envio automático de credenciais</p>
            </div>
          </div>
          <button
            onClick={() => { setShowEvolutionConfig(!showEvolutionConfig); loadEvolutionConfig(); }}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Configurar</span>
          </button>
        </div>

        {/* Formulário de Configuração Manual */}
        {showEvolutionConfig && (
          <div className="mb-4 p-4 bg-slate-900/50 border border-cyan-500/30 rounded-xl">
            <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-cyan-400" />
              Configuração Manual da Evolution API
            </h4>
            
            <div className="space-y-4">
              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  URL da Evolution API *
                </label>
                <input
                  type="text"
                  value={evolutionConfig.url}
                  onChange={(e) => setEvolutionConfig({ ...evolutionConfig, url: e.target.value })}
                  placeholder="https://evolution-api-production-e4df.up.railway.app"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  API Key *
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={evolutionConfig.key}
                    onChange={(e) => setEvolutionConfig({ ...evolutionConfig, key: e.target.value })}
                    placeholder="xK9vP2mQ8sR4fT7jW5nB3hC6dM1yG0zL"
                    className="w-full px-4 py-2 pr-10 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Instance */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Nome da Instância
                </label>
                <input
                  type="text"
                  value={evolutionConfig.instance}
                  onChange={(e) => setEvolutionConfig({ ...evolutionConfig, instance: e.target.value })}
                  placeholder="a2"
                  className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* Mensagem de Status */}
              {configMessage && (
                <div className={`p-3 rounded-lg ${configMessage.type === 'success' ? 'bg-green-500/20 border border-green-500/30 text-green-400' : 'bg-red-500/20 border border-red-500/30 text-red-400'}`}>
                  {configMessage.text}
                </div>
              )}

              {/* Botões */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={saveEvolutionConfig}
                  disabled={savingConfig}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingConfig ? 'Salvando...' : 'Salvar Configuração'}
                </button>
                <button
                  onClick={testEvolutionConnection}
                  disabled={savingConfig}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <MessageCircle className="w-4 h-4" />
                  Testar Envio
                </button>
                <button
                  onClick={checkWhatsappStatus}
                  className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Atualizar Status
                </button>
              </div>

              {/* Dica */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-xs text-blue-400">
                  💡 <strong>Dica:</strong> Use os valores padrão que já estão preenchidos ou insira suas próprias credenciais da Evolution API.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Configuração */}
          <div className={`p-3 sm:p-4 rounded-xl border ${whatsappStatus.configured ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              {whatsappStatus.configured ? (
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
              )}
              <span className={`text-xs sm:text-sm font-medium ${whatsappStatus.configured ? 'text-green-400' : 'text-red-400'}`}>
                Configuração
              </span>
            </div>
            <p className="text-xs text-gray-400">
              {whatsappStatus.configured ? 'Evolution API configurada' : 'Clique em "Configurar" acima'}
            </p>
          </div>

          {/* Status de Conexão */}
          <div className={`p-3 sm:p-4 rounded-xl border ${whatsappStatus.active ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              {whatsappStatus.active ? (
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
              )}
              <span className={`text-xs sm:text-sm font-medium ${whatsappStatus.active ? 'text-green-400' : 'text-red-400'}`}>
                Conexão
              </span>
            </div>
            <p className="text-xs text-gray-400">
              {whatsappStatus.active ? 'WhatsApp conectado' : 'WhatsApp desconectado'}
            </p>
          </div>
        </div>

        {/* Instance Info */}
        {whatsappStatus.instance && (
          <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-xs sm:text-sm text-blue-400">
              📱 Instância: <span className="font-mono">{whatsappStatus.instance}</span> | Estado: <span className="font-semibold">{whatsappStatus.connection_state}</span>
            </p>
          </div>
        )}

        {!whatsappStatus.configured && !showEvolutionConfig && (
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs sm:text-sm text-yellow-400">
              ⚠️ Clique no botão <strong>"Configurar"</strong> acima para inserir as credenciais da Evolution API
            </p>
          </div>
        )}
      </div>

      {/* Webhook Logs */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">Logs de Webhook</h3>
          <button
            onClick={fetchWebhookLogs}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>

        {logs.length === 0 ? (
          <div className="text-center py-12">
            <Webhook className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Nenhum webhook recebido ainda</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {logs.map((log) => (
              <div
                key={log.id}
                className="bg-slate-900/50 border border-slate-700 rounded-xl p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold border ${getStatusColor(log.status)}`}>
                      {log.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                    <span className="text-gray-400 text-sm">
                      {log.type || 'N/A'}
                    </span>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {new Date(log.created_at).toLocaleString('pt-BR')}
                  </span>
                </div>
                
                {log.error && (
                  <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                    <strong>Erro:</strong> {log.error}
                  </div>
                )}
                
                <details className="mt-2">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                    Ver dados completos
                  </summary>
                  <pre className="mt-2 p-3 bg-slate-950 rounded text-xs text-gray-400 overflow-x-auto">
                    {JSON.stringify(log.data, null, 2)}
                  </pre>
                </details>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebhookConfig;
